// Prebake artifacts store slice.
//
// DATA MODEL: the prebake artifacts that have NO 1:1 core-domain home (time log,
// drilling params, cost-vs-depth, daily reports, planned DvD curve, pad/well
// AFE) live HERE — keyed by the created borefield id — instead of being grafted
// onto the canonical Bore/Borefield types (which map to the DDL + JSON export).
// This is the least-invasive attachment: the demo loads the artifacts intact and
// inspectable, the canonical data model stays pristine, and nothing here ships to
// the backend contract. Deleting the borefield clears its prebake bundle via
// clearPrebakeArtifacts (wired in useBorefieldData.deleteBorefield) so nothing
// orphans here.
//
// UNIT MODEL: store-as-entered + UOM — the artifacts keep their authored units
// and UOM tags verbatim (see types.ts). No normalization here.

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { AfeArtifact, LoadedWell } from "../types";

/** Per-borefield prebake payload: the pad AFE + per-bore artifact bundles. The
 *  inner map is keyed by the created Bore id so a bore detail view can look up
 *  its own time log / cost / drilling / daily / planned-curve. */
export interface PrebakeBundle {
  datasetId: string;
  afe: AfeArtifact;
  /** Keyed by Bore.id → the well's loaded artifacts (everything but trajectory,
   *  which maps to a Survey on the Bore). */
  byBoreId: Record<string, LoadedWell>;
}

export interface PrebakeState {
  /** Keyed by Borefield.id. */
  bundles: Record<string, PrebakeBundle>;
}

const initialState: PrebakeState = {
  bundles: {},
};

const prebakeSlice = createSlice({
  name: "prebake",
  initialState,
  reducers: {
    setPrebakeArtifacts(
      state,
      action: PayloadAction<{ borefieldId: string; bundle: PrebakeBundle }>,
    ) {
      state.bundles[action.payload.borefieldId] = action.payload.bundle;
    },
    clearPrebakeArtifacts(state, action: PayloadAction<string>) {
      delete state.bundles[action.payload];
    },
  },
});

export const { setPrebakeArtifacts, clearPrebakeArtifacts } =
  prebakeSlice.actions;

export const prebakeReducer = prebakeSlice.reducer;

// Selectors
export const selectPrebakeBundles = (state: {
  prebake: PrebakeState;
}): Record<string, PrebakeBundle> => state.prebake.bundles;

export const selectPrebakeBundle =
  (borefieldId: string | undefined) =>
  (state: { prebake: PrebakeState }): PrebakeBundle | undefined =>
    borefieldId ? state.prebake.bundles[borefieldId] : undefined;
